import React, { useState, useEffect, useCallback } from 'react';
import { X, Star } from 'lucide-react';
import { rewardsAPI, redemptionsAPI, Reward, Redemption, StarBalance } from '../services/api';
import { Kid } from '../types/todo.types';

interface RedeemModalProps {
  kid: Kid;
  onClose: () => void;
  onRedeem: () => void;
}

const RedeemModal: React.FC<RedeemModalProps> = ({ kid, onClose, onRedeem }) => {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [balance, setBalance] = useState<StarBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [redeemingId, setRedeemingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [tab, setTab] = useState<'rewards' | 'redeemed'>('rewards');
  // null = not loaded yet; fetched the first time the Redeemed tab is opened
  const [history, setHistory] = useState<Redemption[] | null>(null);
  const [historyError, setHistoryError] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [rewardsData, balanceData] = await Promise.all([
        rewardsAPI.getAll(),
        redemptionsAPI.getStars(kid.name)
      ]);
      setRewards(rewardsData);
      setBalance(balanceData);
    } catch (err) {
      console.error('Error fetching rewards data:', err);
    } finally {
      setLoading(false);
    }
  }, [kid.name]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (tab !== 'redeemed' || history !== null) return;
    setHistoryError(false);
    redemptionsAPI.getHistory(kid.name)
      .then(setHistory)
      .catch(err => {
        console.error('Error fetching redemption history:', err);
        setHistoryError(true);
      });
  }, [tab, history, kid.name]);

  const handleRedeem = async (reward: Reward) => {
    if (!balance || balance.stars < reward.cost) return;

    setRedeemingId(reward._id);
    try {
      const result = await redemptionsAPI.redeem(kid.name, reward._id);
      setBalance({
        stars: result.stars,
        totalEarned: result.totalEarned,
        totalSpent: result.totalSpent
      });
      setSuccessMessage(`${reward.emoji} ${reward.name} redeemed!`);
      setHistory(null);
      onRedeem();
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      console.error('Error redeeming reward:', err);
    } finally {
      setRedeemingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`${kid.color} rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden`}>
        {/* Header */}
        <div className="p-5 pb-3">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full hover:bg-black hover:bg-opacity-10 transition touch-manipulation"
            aria-label="Close"
          >
            <X size={24} className="text-gray-600" />
          </button>

          <div className="flex items-center gap-3 mb-3">
            <span className="text-4xl">{kid.avatar}</span>
            <div>
              <h2 className={`${kid.textColor} font-bold text-2xl`}>{kid.name}</h2>
              {balance && (
                <div className="flex items-center gap-1.5">
                  <Star size={20} className="text-yellow-500 fill-yellow-500" />
                  <span className="text-xl font-bold text-gray-800">{balance.stars} stars</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Success message */}
        {successMessage && (
          <div className="mx-5 mb-3 p-3 bg-green-100 border border-green-300 rounded-xl text-center">
            <span className="text-green-800 font-semibold text-lg">{successMessage}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="mx-5 mb-3 flex gap-1 p-1 bg-black bg-opacity-10 rounded-xl" role="tablist">
          {(['rewards', 'redeemed'] as const).map(t => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={`flex-1 py-2 rounded-lg font-semibold text-sm transition touch-manipulation ${
                tab === t ? 'bg-white text-gray-800 shadow' : 'text-gray-600'
              }`}
            >
              {t === 'rewards' ? 'Rewards' : 'Redeemed'}
            </button>
          ))}
        </div>

        {tab === 'redeemed' ? (
          <div className="px-5 pb-5 space-y-2.5 max-h-96 overflow-y-auto">
            {historyError ? (
              <div className="text-center py-8">
                <p className="text-gray-600 font-medium">Couldn't load redemptions.</p>
              </div>
            ) : history === null ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 border-b-3 border-gray-500 mx-auto mb-3"></div>
                <p className="text-gray-600 font-medium">Loading redemptions...</p>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 font-medium">Nothing redeemed yet.</p>
              </div>
            ) : (
              history.map(r => {
                const emoji = rewards.find(rw => rw._id === r.rewardId)?.emoji;
                return (
                  <div key={r._id} className="flex items-center justify-between p-3.5 rounded-xl bg-white">
                    <div className="flex items-center gap-3">
                      {emoji && <span className="text-3xl">{emoji}</span>}
                      <div>
                        <div className="font-semibold text-gray-800 text-base">{r.rewardName}</div>
                        <div className="text-sm text-gray-500">
                          {new Date(r.redeemedAt).toLocaleString(undefined, {
                            day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                      <Star size={14} className="text-yellow-500 fill-yellow-500" />
                      <span>-{r.starsCost}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : (
        /* Rewards list */
        <div className="px-5 pb-5 space-y-2.5">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-3 border-gray-500 mx-auto mb-3"></div>
              <p className="text-gray-600 font-medium">Loading rewards...</p>
            </div>
          ) : rewards.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 font-medium">No rewards available yet.</p>
            </div>
          ) : (
            rewards.map(reward => {
              const canAfford = balance !== null && balance.stars >= reward.cost;
              const isRedeeming = redeemingId === reward._id;

              return (
                <div
                  key={reward._id}
                  className={`flex items-center justify-between p-3.5 rounded-xl bg-white ${
                    canAfford ? '' : 'opacity-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{reward.emoji}</span>
                    <div>
                      <div className="font-semibold text-gray-800 text-base">{reward.name}</div>
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Star size={14} className="text-yellow-500 fill-yellow-500" />
                        <span>{reward.cost} stars</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleRedeem(reward)}
                    disabled={!canAfford || isRedeeming}
                    className={`px-4 py-2.5 rounded-xl font-semibold text-sm transition touch-manipulation ${
                      canAfford
                        ? 'bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600 text-yellow-900'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isRedeeming ? '...' : 'Redeem'}
                  </button>
                </div>
              );
            })
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default RedeemModal;
